import { Check, ExternalLink } from "lucide-react";
import { useState } from "react";

import { SiteGallery } from "@/components/SiteGallery";
import { ImageField } from "@/components/ImageField";
import {
  Button,
  ErrorBanner,
  FormSection,
  Input,
  PageTitle,
  Select,
  Spinner,
  Switch,
} from "@/components/ui";
import { toApiError } from "@/lib/api";
import { useT } from "@/lib/i18n";
import { useSite, useSiteImageRemove, useSiteUpdate } from "@/lib/queries";
import type { SiteTheme } from "@/lib/types";

export default function SiteSettings() {
  const t = useT();
  const { data, isLoading, isError } = useSite();

  if (isLoading) {
    return (
      <div className="flex min-h-[60dvh] items-center justify-center">
        <Spinner className="size-8" />
      </div>
    );
  }
  if (isError || !data) {
    return (
      <div className="p-4 lg:p-8">
        <ErrorBanner message={t("site.loadFailed")} />
      </div>
    );
  }

  // key: ao trocar de oficina o formulário precisa remontar com os
  // valores da casa nova, e não manter o rascunho da anterior.
  return <Formulario key={data.public_url} inicial={data} />;
}

function Formulario({ inicial }: { inicial: SiteTheme }) {
  const t = useT();
  const salvar = useSiteUpdate();
  const remover = useSiteImageRemove();

  const [campos, setCampos] = useState(inicial);
  const [logo, setLogo] = useState<File | null>(null);
  const [cover, setCover] = useState<File | null>(null);
  const [salvou, setSalvou] = useState(false);

  const set = <K extends keyof SiteTheme>(chave: K, valor: SiteTheme[K]) => {
    setCampos((c) => ({ ...c, [chave]: valor }));
    setSalvou(false);
  };

  const erro = salvar.isError ? toApiError(salvar.error) : null;

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    salvar.mutate(
      {
        name: campos.name,
        template_key: campos.template_key,
        primary_color: campos.primary_color,
        accent_color: campos.accent_color,
        font_display: campos.font_display,
        headline: campos.headline,
        subheadline: campos.subheadline,
        about: campos.about,
        show_prices: campos.show_prices,
        show_professional_photos: campos.show_professional_photos,
        instagram: campos.instagram,
        facebook: campos.facebook,
        // Só vão quando o dono escolheu arquivo novo — ver useSiteUpdate.
        ...(logo ? { logo } : {}),
        ...(cover ? { cover } : {}),
      },
      {
        onSuccess: (novo) => {
          setCampos(novo);
          setLogo(null);
          setCover(null);
          setSalvou(true);
        },
      },
    );
  };

  return (
    <div className="p-4 lg:p-8">
      <PageTitle
        eyebrow={t("site.pageTitle")}
        title={t("site.title")}
        subtitle={t("site.subtitle")}
        action={
          <a
            href={inicial.public_url}
            target="_blank"
            rel="noopener"
            className="inline-flex items-center gap-2 text-sm text-racing-500 hover:underline"
          >
            {t("site.viewMyPage")} <ExternalLink size={14} />
          </a>
        }
      />

      <form onSubmit={submit} className="max-w-3xl space-y-5">
        <FormSection title={t("site.identityTitle")} description={t("site.identity")}>
          <Input
            label={t("signup.shopName")}
            required
            maxLength={120}
            value={campos.name}
            onChange={(e) => set("name", e.target.value)}
          />
          <ImageField
            label={t("site.logo")}
            hint={t("site.logoHint")}
            shape="square"
            value={campos.logo}
            file={logo}
            onPick={setLogo}
            onRemove={() => remover.mutate("logo", { onSuccess: setCampos })}
          />
          <ImageField
            label={t("site.cover")}
            hint={t("site.coverHint")}
            value={campos.cover}
            file={cover}
            onPick={setCover}
            onRemove={() => remover.mutate("cover", { onSuccess: setCampos })}
          />
        </FormSection>

        <SiteGallery onCover={(url) => {
          setCampos((current) => ({ ...current, cover: url }));
          setCover(null);
        }} />

        <FormSection title={t("site.style")} description={t("site.lookHint")}>
          <div>
            <span className="eyebrow mb-1.5 block text-steel-dim">{t("site.template")}</span>
            <div className="grid gap-2 sm:grid-cols-3">
              {campos.templates.map((t) => (
                <button
                  key={t.value}
                  type="button"
                  onClick={() => set("template_key", t.value)}
                  aria-pressed={campos.template_key === t.value}
                  className={
                    campos.template_key === t.value
                      ? "rounded-[4px] border border-nitro-500 bg-nitro-500/10 p-3 text-left"
                      : "rounded-[4px] border border-line p-3 text-left hover:border-steel-dim"
                  }
                >
                  <Amostra template={t.value} campos={campos} />
                  <span className="mt-2 flex items-center gap-1.5 text-sm font-medium">
                    {campos.template_key === t.value && (
                      <Check size={14} className="text-nitro-500" />
                    )}
                    {t.label}
                  </span>
                </button>
              ))}
            </div>
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <Input
              label={t("site.primaryColor")}
              type="color"
              className="h-10 p-1"
              value={campos.primary_color}
              onChange={(e) => set("primary_color", e.target.value)}
              hint={t("site.primaryColorHint")}
            />
            <Input
              label={t("site.accentColor")}
              type="color"
              className="h-10 p-1"
              value={campos.accent_color}
              onChange={(e) => set("accent_color", e.target.value)}
              hint={t("site.buttonsLinks")}
            />
          </div>

          <Select
            label={t("site.displayFont")}
            value={campos.font_display}
            onChange={(e) => set("font_display", e.target.value)}
          >
            {campos.fonts.map((f) => (
              <option key={f.value} value={f.value}>
                {f.label}
              </option>
            ))}
          </Select>
        </FormSection>

        <FormSection title={t("site.textsTitle")} description={t("site.beforeBooking")}>
          <Input
            label={t("site.headline")}
            maxLength={120}
            placeholder={campos.name}
            value={campos.headline}
            onChange={(e) => set("headline", e.target.value)}
            hint={t("site.headlineHint")}
          />
          <Input
            label={t("site.subheadline")}
            maxLength={200}
            placeholder={t("site.subheadlinePlaceholder")}
            value={campos.subheadline}
            onChange={(e) => set("subheadline", e.target.value)}
            hint={t("site.subheadlineHint")}
          />
          <label className="block">
            <span className="eyebrow mb-1.5 block text-steel-dim">{t("site.about")}</span>
            <textarea
              rows={4}
              value={campos.about}
              onChange={(e) => set("about", e.target.value)}
              placeholder={t("site.aboutPlaceholder")}
              className="w-full rounded-[4px] border border-line bg-carbon-900 px-3 py-2.5 text-steel outline-none transition placeholder:text-steel-dim focus:border-nitro-500"
            />
          </label>
        </FormSection>

        <FormSection title={t("site.whatToShow")} description={t("site.visibilityHint")}>
          <Switch
            checked={campos.show_prices}
            onChange={(v) => set("show_prices", v)}
            label={t("site.showPrices")}
            hint={t("site.showPricesHint")}
          />
          <Switch
            checked={campos.show_professional_photos}
            onChange={(v) => set("show_professional_photos", v)}
            label={t("site.showTeam")}
            hint={t("site.showTeamHint")}
          />
        </FormSection>

        <FormSection title={t("site.socialTitle")} description={t("site.socialHint")}>
          <div className="grid gap-3 sm:grid-cols-2">
            <Input
              label="Instagram"
              placeholder={t("site.handlePlaceholder")}
              value={campos.instagram}
              onChange={(e) => set("instagram", e.target.value)}
            />
            <Input
              label="Facebook"
              placeholder={t("site.handlePlain")}
              value={campos.facebook}
              onChange={(e) => set("facebook", e.target.value)}
            />
          </div>
        </FormSection>

        {erro && <ErrorBanner message={erro.message} />}

        <div className="sticky bottom-0 flex items-center gap-3 border-t border-line bg-carbon-900/95 py-4 backdrop-blur">
          <Button type="submit" variant="racing" disabled={salvar.isPending}>
            {salvar.isPending ? (
              <Spinner className="size-4 border-carbon-900/30 border-t-carbon-900" />
            ) : (
              t("site.savePublish")
            )}
          </Button>
          {salvou && (
            <span className="flex items-center gap-1.5 text-sm text-emerald-400">
              <Check size={16} /> {t("site.published")}
            </span>
          )}
        </div>
      </form>
    </div>
  );
}

/** Miniatura do template com as cores atuais — evita ter de abrir o site
 *  para descobrir o que "Escuro" significa. */
function Amostra({ template, campos }: { template: string; campos: SiteTheme }) {
  const fundo = template === "moderno" ? "#ffffff" : template === "dark" ? "#000000" : "#0c0b0a";
  const texto = template === "moderno" ? "#14130f" : "#fafafa";

  return (
    <div className="overflow-hidden rounded-[3px] border border-line" style={{ background: fundo }}>
      <div className="h-6" style={{ background: campos.primary_color }} />
      <div className="space-y-1 p-2">
        <div className="h-1.5 w-2/3 rounded-full" style={{ background: texto, opacity: 0.8 }} />
        <div className="h-1.5 w-1/2 rounded-full" style={{ background: texto, opacity: 0.35 }} />
        <div
          className="mt-2 h-3 w-14 rounded-[2px]"
          style={{ background: campos.accent_color }}
        />
      </div>
    </div>
  );
}
